<template>
    <transition name="upload-section" appear>
        <div v-if="fileListArray.length" class="uploaded-files-section" data-testid="uploaded-files-section">
            <Alert closable @onClose="dismissTemplatesList">
                <p class="title">Recently uploaded templates</p>

                <template #desc>
                    <div class="uploaded-files-section__files-list" data-testid="uploaded-files-section__files-list">
                        <transition-group name="file-item" tag="div" class="file-list-container">
                            <div
                                v-for="file in fileListArray"
                                :key="file.uid"
                                ref="fileItem"
                                class="upload-file-item"
                                :data-testid="`upload-file-item-${file.name}`"
                            >
                                <div class="file-info">
                                    <HTML5Icon
                                        v-if="file?.adType === TemplateType.HTML5"
                                        width="15"
                                        height="15"
                                        :data-testid="`upload-file-icon-${file.adType}`"
                                    />

                                    <PhotoshopIcon
                                        v-else-if="file?.adType === TemplateType.PSD"
                                        width="15"
                                        height="15"
                                        :data-testid="`upload-file-icon-${file.adType}`"
                                    />

                                    <AEIcon
                                        v-else-if="file?.adType === TemplateType.AE"
                                        width="15"
                                        height="15"
                                        :data-testid="`upload-file-icon-${file.adType}`"
                                    />

                                    <InDesignIcon
                                        v-else-if="file?.adType === TemplateType.INDESIGN"
                                        width="15"
                                        height="15"
                                        :data-testid="`upload-file-icon-${file.adType}`"
                                    />

                                    <Icon
                                        v-else
                                        type="ios-folder"
                                        size="15"
                                        color="#5E00B5"
                                        data-testid="upload-file-icon-folder"
                                    />

                                    <Ellipsis
                                        :key="ellipsisKey + file.status"
                                        :text="file.name"
                                        :lines="1"
                                        class="file-info-text"
                                        tooltip
                                        data-testid="upload-file-name"
                                        transfer
                                    />

                                    <Icon
                                        v-if="file.status === 'UPLOADED'"
                                        type="ios-checkmark-circle"
                                        color="#19be6b"
                                        data-testid="file-uploaded-icon"
                                    />

                                    <Icon
                                        v-if="file.status === 'FAILED'"
                                        type="ios-close-circle"
                                        color="#df2f2f"
                                        data-testid="file-failed-icon"
                                    />
                                </div>

                                <Progress
                                    hide-info
                                    :percent="file?.percentage"
                                    :stroke-width="3"
                                    :status="getProgressStatus(file.status)"
                                    data-testid="file-upload-progress"
                                />

                                <transition name="error-message" mode="out-in">
                                    <div v-if="file.status === 'FAILED'" :key="file.uid" class="error-message">
                                        <p data-testid="upload-file-error">
                                            {{ formatErrorMessage(file?.errorMessage) }}
                                        </p>
                                    </div>
                                </transition>
                            </div>
                        </transition-group>
                    </div>
                </template>
            </Alert>
        </div>
    </transition>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Alert } from "view-ui-plus";

import type { MasterTemplateFile } from "@/types/types";

import HTML5Icon from "@/assets/html5_icon.svg";
import PhotoshopIcon from "@/assets/photoshop_icon.svg";
import AEIcon from "@/assets/ae_icon.svg";
import InDesignIcon from "@/assets/Adobe_Indesign_CC_2026_icon.svg";
import { TemplateType } from "shared-utils/enums/masterTemplate";

export default defineComponent({
    name: "MasterTemplateUploadList",
    components: {
        HTML5Icon,
        PhotoshopIcon,
        AEIcon,
        InDesignIcon,
        Alert
    },
    props: {
        fileList: {
            type: Object as () => Record<string, MasterTemplateFile>,
            required: true
        }
    },

    emits: ["update:fileList"],
    data() {
        return {
            ellipsisKey: 0,
            resizeObserver: null as ResizeObserver | null,
            TemplateType
        };
    },

    computed: {
        fileListArray() {
            return Object.values(this.fileList).sort((a, b) => {
                if (a.date && b.date) {
                    return b.date.getTime() - a.date.getTime();
                }
                return 0;
            });
        }
    },

    mounted() {
        // the ellipsis component has a reactivity issue
        this.initResizeObserver();
    },

    updated() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        this.initResizeObserver();
    },

    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },

    methods: {
        initResizeObserver() {
            this.resizeObserver = new ResizeObserver(() => {
                this.ellipsisKey = Date.now();
            });

            const fileItems = this.$refs.fileItem as HTMLElement[];
            if (fileItems) {
                fileItems.forEach((item: HTMLElement) => {
                    this.resizeObserver?.observe(item);
                });
            }
        },

        dismissTemplatesList() {
            this.$emit("update:fileList", {});
        },

        getProgressStatus(status: MasterTemplateFile["status"]) {
            switch (status) {
                case "UPLOADED":
                    return "success";
                case "FAILED":
                    return "wrong";
                case "UPLOADING":
                    return "active";
                default:
                    return "normal";
            }
        },

        formatErrorMessage(message?: string) {
            if (!message) return "An unknown error occurred.";

            return message
                .replace("GraphQL error: ", "")
                .replace("ValidationError:", "")
                .replace("(INVALID)", "")
                .trim();
        }
    }
});
</script>

<style lang="scss" scoped>
@use "@style/mixins" as *;
@use "@style/variables" as *;

.uploaded-files-section {
    padding: 10px 20px 16px;

    .uploaded-files-section__files-list {
        max-height: 250px;
        overflow: auto;

        .file-list-container {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 10px;

            @media screen and (max-width: 1440px) {
                grid-template-columns: repeat(3, 1fr);
            }

            @media screen and (max-width: 900px) {
                grid-template-columns: repeat(2, 1fr);
            }

            @media screen and (max-width: 700px) {
                grid-template-columns: repeat(1, 1fr);
                max-height: 200px;
                overflow: auto;
            }
        }

        @media screen and (max-height: 800px) {
            max-height: 150px;
        }

        .upload-file-item {
            background-color: #dff3ff;
            border-radius: 4px;
            height: max-content;

            padding: 10px 10px 2px;

            @include dark {
                background-color: $darktheme40;
            }

            .file-info {
                display: flex;
                align-items: center;
                gap: 10px;

                @include dark {
                    color: $white;
                }

                .file-info-text {
                    flex-grow: 1;
                }
            }
        }
    }

    .error-message {
        color: var(--wpp-danger-color-400);
        padding-top: 6px;
        font-size: 12px;
    }

    .title {
        margin-bottom: 16px;
        font-size: 14px;
        font-weight: 500;

        @include dark {
            color: $white;
        }
    }
}

.upload-section-enter-active {
    transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.upload-section-leave-active {
    transition: all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1);
    overflow: hidden;
}

.upload-section-enter-from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
    max-height: 0;
}

.upload-section-enter-to {
    opacity: 1;
    transform: translateY(0) scale(1);
    max-height: 1000px;
}

.upload-section-leave-from {
    opacity: 1;
    transform: translateY(0) scale(1);
    max-height: 1000px;
}

.upload-section-leave-to {
    opacity: 0;
    transform: translateY(-15px) scale(0.95);
    max-height: 0;
    margin-top: 0;
    margin-bottom: 0;
    padding-top: 0;
    padding-bottom: 0;
}

.file-item-enter-active {
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.file-item-leave-active {
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    position: absolute;
    width: calc(33.333% - 7px);
}

.file-item-enter-from {
    opacity: 0;
    transform: translateX(-20px) scale(0.9);
}

.file-item-leave-to {
    opacity: 0;
    transform: translateX(20px) scale(0.9);
}

.file-item-move {
    transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.error-message-enter-active,
.error-message-leave-active {
    transition: all 0.25s ease-in-out;
}

.error-message-enter-from,
.error-message-leave-to {
    opacity: 0;
    transform: translateY(-10px);
    max-height: 0;
    padding-top: 0;
}

.error-message-enter-to,
.error-message-leave-from {
    opacity: 1;
    transform: translateY(0);
    max-height: 50px;
    padding-top: 5px;
}
</style>
